import { Account } from '../../cache/Account';
import { AccountFeature } from '../../cache/AccountEnums';
import { Resources } from '../../properties/Resources';
import { AccountSummaryTablesEnum } from '../Columns/AccountSummaryTablesEnum';
import { TvAccountColumns } from '../Columns/TvAccountColumns';
import { TvAccountActivityData } from '../TradingViewPrimitives/AccountData/TvAccountActivityData';
import { TvAccountGeneralData } from '../TradingViewPrimitives/AccountData/TvAccountGeneralData';
import { TvAccountMarginData } from '../TradingViewPrimitives/AccountData/TvAccountMarginData';
import { TvAccountTodaysResultData } from '../TradingViewPrimitives/AccountData/TvAccountTodaysResultData';
import { type AccountManagerPage, type AccountManagerSummaryField, type IBrokerConnectionAdapterHost, type IWatchedValue, type IWatchedValueReadonly, StandardFormatterName, type AccountManagerTable } from '../charting_library';

export class TvAccountTableManger {
    private readonly generalData: TvAccountGeneralData;
    private readonly marginData: TvAccountMarginData;
    private readonly activityData: TvAccountActivityData;
    private readonly todaysResultsData: TvAccountTodaysResultData;

    private readonly balanceValue: IWatchedValue<number>;
    private readonly projectedBalanceValue: IWatchedValue<number>;
    private readonly openGrossPLValue: IWatchedValue<number>;

    private readonly generalDelegate: any;
    private readonly marginDelegate: any;
    private readonly activityDelegate: any;
    private readonly todaysResultsDelegate: any;

    private needUpdateEquity = false;
    private needUpdateMarginAvailable = false;
    private prevEquity: number = 0;
    private prevMarginAvailable: number = 0;

    private curAccount: Account;
    private readonly host: IBrokerConnectionAdapterHost;

    constructor (host: IBrokerConnectionAdapterHost) {
        this.host = host;

        this.generalData = new TvAccountGeneralData();
        this.marginData = new TvAccountMarginData();
        this.activityData = new TvAccountActivityData();
        this.todaysResultsData = new TvAccountTodaysResultData();

        this.generalDelegate = host.factory.createDelegate();
        this.marginDelegate = host.factory.createDelegate();
        this.activityDelegate = host.factory.createDelegate();
        this.todaysResultsDelegate = host.factory.createDelegate();

        this.balanceValue = host.factory.createWatchedValue(this.generalData.balance);
        this.projectedBalanceValue = host.factory.createWatchedValue(this.generalData.projectedBalance);
        this.openGrossPLValue = host.factory.createWatchedValue(this.activityData.openGrossPL);

        this.generalDelegate.subscribe(null, t => {
            this.balanceValue.setValue(t.balance),
            this.projectedBalanceValue.setValue(t.projectedBalance);
            // this.openGrossPLValue.setValue(t.openGrossPL);
        });

        this.activityDelegate.subscribe(null, t => {
            this.openGrossPLValue.setValue(t.openGrossPL);
        });
    }

    public subscribeEquity (): void {
        this.needUpdateEquity = true;
    }

    public unsubscribeEquity (): void {
        this.needUpdateEquity = false;
    }

    public subscribeMarginAvailable (symbol: string): void {
        this.needUpdateMarginAvailable = true;
    }

    public unsubscribeMarginAvailable (symbol: string): void {
        this.needUpdateMarginAvailable = false;
    }

    public setCurrentAccount (account: Account) {
        this.curAccount = account;
    }

    public updateTable (): void {
        this.updateGeneralTable();
        this.updateMarginTable();
        this.updateAccountActivityTable();
        this.updateTodaysResultsTable();

        if (this.needUpdateEquity) {
            const acc = this.curAccount;
            const balance = Account.GetAccountFeature(AccountFeature.Balance, acc, acc.assetBalanceDefault);
            const pnl = acc.Profit;
            const equity = balance + pnl;
            if (equity !== this.prevEquity) this.host.equityUpdate(equity);
            this.prevEquity = equity;
        }

        if (this.needUpdateMarginAvailable) {
            const acc = this.curAccount;
            const marginAvailable = Account.GetAccountFeature(AccountFeature.MarginAvailable, acc, acc.assetBalanceDefault);
            if (marginAvailable !== this.prevMarginAvailable) this.host.marginAvailableUpdate(marginAvailable);
            this.prevMarginAvailable = marginAvailable;
        }
    }

    private updateGeneralTable (): void {
        const acc = this.curAccount;
        let isNeedUpdate = false;
        const balance = Account.GetAccountFeature(AccountFeature.Balance, acc, acc.assetBalanceDefault);
        if (balance !== this.generalData.balance) {
            this.generalData.balance = balance;
            isNeedUpdate = true;
        }

        const projectedBalance = Account.GetAccountFeature(AccountFeature.ProjectedBalance, acc, acc.assetBalanceDefault);
        if (projectedBalance !== this.generalData.projectedBalance) {
            this.generalData.projectedBalance = projectedBalance;
            isNeedUpdate = true;
        }

        const availableFunds = Account.GetAccountFeature(AccountFeature.AvailableFunds, acc, acc.assetBalanceDefault);
        if (availableFunds !== this.generalData.availableFunds) {
            this.generalData.availableFunds = availableFunds;
            isNeedUpdate = true;
        }

        const blockedBalance = Account.GetAccountFeature(AccountFeature.BlockedBalance, acc, acc.assetBalanceDefault);
        if (blockedBalance !== this.generalData.blockedBalance) {
            this.generalData.blockedBalance = blockedBalance;
            isNeedUpdate = true;
        }

        const unsettledCash = Account.GetAccountFeature(AccountFeature.UnsettledCash, acc, acc.assetBalanceDefault);
        if (unsettledCash !== this.generalData.unsettledCash) {
            this.generalData.unsettledCash = unsettledCash;
            isNeedUpdate = true;
        }

        if (isNeedUpdate) {
            this.generalDelegate.fire(this.generalData);
        }
    }

    private updateMarginTable (): void {
        const acc = this.curAccount;
        let isNeedUpdate = false;

        const initialMarginReq = Account.GetAccountFeature(AccountFeature.MarginUsed, acc, acc.assetBalanceDefault);
        if (initialMarginReq !== this.marginData.initialMarginReq) {
            this.marginData.initialMarginReq = initialMarginReq;
            isNeedUpdate = true;
        }

        const initialMarginReqPercent = Account.GetAccountFeature(AccountFeature.MarginUsedPercent, acc, acc.assetBalanceDefault);
        if (initialMarginReqPercent !== this.marginData.initialMarginReqPercent) {
            this.marginData.initialMarginReqPercent = initialMarginReqPercent;
            isNeedUpdate = true;
        }

        const maintMarginReq = Account.GetAccountFeature(AccountFeature.TotalMaintReq, acc, acc.assetBalanceDefault);
        if (maintMarginReq !== this.marginData.maintMarginReq) {
            this.marginData.maintMarginReq = maintMarginReq;
            isNeedUpdate = true;
        }

        const maintMarginReqPercent = Account.GetAccountFeature(AccountFeature.RiskLevelPercent, acc, acc.assetBalanceDefault);
        if (maintMarginReqPercent !== this.marginData.maintMarginReqPercent) {
            this.marginData.maintMarginReqPercent = maintMarginReqPercent;
            isNeedUpdate = true;
        }

        const marginWarningLevel = Account.GetAccountFeature(AccountFeature.MarginWarningLevel, acc, acc.assetBalanceDefault);
        if (marginWarningLevel !== this.marginData.marginWarningLevel) {
            this.marginData.marginWarningLevel = marginWarningLevel;
            isNeedUpdate = true;
        }

        const marginAvailable = Account.GetAccountFeature(AccountFeature.MarginAvailable, acc, acc.assetBalanceDefault);
        if (marginAvailable !== this.marginData.marginAvailable) {
            this.marginData.marginAvailable = marginAvailable;
            isNeedUpdate = true;
        }

        const warningMargin = Account.GetAccountFeature(AccountFeature.WarningMargin, acc, acc.assetBalanceDefault);
        if (warningMargin !== this.marginData.warningMargin) {
            this.marginData.warningMargin = warningMargin;
            isNeedUpdate = true;
        }

        const blockedForStocks = Account.GetAccountFeature(AccountFeature.BlockedForStocks, acc, acc.assetBalanceDefault);
        if (blockedForStocks !== this.marginData.blockedForStocks) {
            this.marginData.blockedForStocks = blockedForStocks;
            isNeedUpdate = true;
        }

        const stocksOrdersReq = Account.GetAccountFeature(AccountFeature.StocksOrdersReq, acc, acc.assetBalanceDefault);
        if (stocksOrdersReq !== this.marginData.stocksOrdersReq) {
            this.marginData.stocksOrdersReq = stocksOrdersReq;
            isNeedUpdate = true;
        }

        if (isNeedUpdate) {
            this.marginDelegate.fire(this.marginData);
        }
    }

    private updateAccountActivityTable (): void {
        const acc = this.curAccount;
        let isNeedUpdate = false;
        const openGrossPL = Account.GetAccountFeature(AccountFeature.OpenGrossPL, acc, acc.assetBalanceDefault);
        if (openGrossPL !== this.activityData.openGrossPL) {
            this.activityData.openGrossPL = openGrossPL;
            isNeedUpdate = true;
        }

        const openNetPL = Account.GetAccountFeature(AccountFeature.OpenNetPL, acc, acc.assetBalanceDefault);
        if (openNetPL !== this.activityData.openNetPL) {
            this.activityData.openNetPL = openNetPL;
            isNeedUpdate = true;
        }

        const openPositionsCount = Account.GetAccountFeature(AccountFeature.OpenPostionsNumber, acc, acc.assetBalanceDefault);
        if (openPositionsCount !== this.activityData.openPositionsCount) {
            this.activityData.openPositionsCount = openPositionsCount;
            isNeedUpdate = true;
        }

        const openOrdersCount = Account.GetAccountFeature(AccountFeature.OpenOrdersNumber, acc, acc.assetBalanceDefault);
        if (openOrdersCount !== this.activityData.openOrdersCount) {
            this.activityData.openOrdersCount = openOrdersCount;
            isNeedUpdate = true;
        }

        if (isNeedUpdate) {
            this.activityDelegate.fire(this.activityData);
        }
    }

    private updateTodaysResultsTable (): void {
        const acc = this.curAccount;
        let isNeedUpdate = false;

        const todaysGross = Account.GetAccountFeature(AccountFeature.TodayGrossPNL, acc, acc.assetBalanceDefault);
        if (todaysGross !== this.todaysResultsData.todaysGross) {
            this.todaysResultsData.todaysGross = todaysGross;
            isNeedUpdate = true;
        }

        const todaysNet = Account.GetAccountFeature(AccountFeature.TodaysNetProfit, acc, acc.assetBalanceDefault);
        if (todaysNet !== this.todaysResultsData.todaysNet) {
            this.todaysResultsData.todaysNet = todaysNet;
            isNeedUpdate = true;
        }

        const todaysFees = Account.GetAccountFeature(AccountFeature.TodaysFee, acc, acc.assetBalanceDefault);
        if (todaysFees !== this.todaysResultsData.todaysFees) {
            this.todaysResultsData.todaysFees = todaysFees;
            isNeedUpdate = true;
        }

        const todaysVolume = Account.GetAccountFeature(AccountFeature.TodayVolume, acc, acc.assetBalanceDefault);
        if (todaysVolume !== this.todaysResultsData.todaysVolume) {
            this.todaysResultsData.todaysVolume = todaysVolume;
            isNeedUpdate = true;
        }

        const todaysTrades = Account.GetAccountFeature(AccountFeature.TodayTrades, acc, acc.assetBalanceDefault);
        if (todaysTrades !== this.todaysResultsData.todaysTrades) {
            this.todaysResultsData.todaysTrades = todaysTrades;
            isNeedUpdate = true;
        }

        if (isNeedUpdate) {
            this.todaysResultsDelegate.fire(this.todaysResultsData);
        }
    }

    public getSummary (): AccountManagerSummaryField[] {
        const balanceItem = this.createSummaryItem(
            Resources.getResource('panel.accounts.Balance'),
            this.balanceValue);

        const projectedBalanceItem = this.createSummaryItem(
            Resources.getResource('panel.accounts.CurBalance'),
            this.projectedBalanceValue);

        const openGrossPL = this.createSummaryItem(
            Resources.getResource('panel.accounts.Profit'),
            this.openGrossPLValue);

        return [balanceItem, projectedBalanceItem, openGrossPL];
    }

    public getPages (): AccountManagerPage[] {
        const tables = this.getTables();
        if (tables.length === 0) {
            return [];
        }

        return [{
            id: 'accountsummary',
            title: Resources.getResource('TvTradingPlatform.AccountSummary.Title'),
            tables
        }];
    }

    private getTables (): AccountManagerTable[] {
        const generalTable = this.createTableItem(AccountSummaryTablesEnum.General);
        const marginTable = this.createTableItem(AccountSummaryTablesEnum.Margin);
        const accountActivityTable = this.createTableItem(AccountSummaryTablesEnum.AccountActivity);
        const todaysResultsTable = this.createTableItem(AccountSummaryTablesEnum.TodaysResults);

        const tables = [generalTable, marginTable, accountActivityTable, todaysResultsTable];
        return tables.filter(t => t !== null);
    }

    private createTableItem (tableType: AccountSummaryTablesEnum): AccountManagerTable | null {
        const columns = TvAccountColumns.getColumns(tableType);
        if (columns.length === 0) {
            return null;
        }

        const changeDelegate = this.getChangeDelegate(tableType);

        const table: AccountManagerTable = {
            id: tableType.toString(),
            title: this.getTableTitle(tableType),
            columns: TvAccountColumns.getColumns(tableType),
            getData: this.getDataDelegate(tableType),
            changeDelegate
        };

        return table;
    }

    public getDataDelegate = (tableType: AccountSummaryTablesEnum): (paginationLastId?: string | number) => Promise<Array<{}>> => {
        switch (tableType) {
        case AccountSummaryTablesEnum.General:
            return this.getGeneralData;
        case AccountSummaryTablesEnum.Margin:
            return this.getMarginData;
        case AccountSummaryTablesEnum.AccountActivity:
            return this.getActivityData;
        case AccountSummaryTablesEnum.TodaysResults:
            return this.getTodayResultsData;
        default:
            throw new Error('Invalid Table Type');
        }
    };

    private readonly getGeneralData = async (paginationLastId?: string | number): Promise<Array<{}>> => {
        return await Promise.resolve([this.generalData]);
    };

    private readonly getMarginData = async (paginationLastId?: string | number): Promise<Array<{}>> => {
        return await Promise.resolve([this.marginData]);
    };

    private readonly getActivityData = async (paginationLastId?: string | number): Promise<Array<{}>> => {
        return await Promise.resolve([this.activityData]);
    };

    private readonly getTodayResultsData = async (paginationLastId?: string | number): Promise<Array<{}>> => {
        return await Promise.resolve([this.todaysResultsData]);
    };

    private getChangeDelegate (tableType: AccountSummaryTablesEnum): any {
        switch (tableType) {
        case AccountSummaryTablesEnum.General:
            return this.generalDelegate;
        case AccountSummaryTablesEnum.Margin:
            return this.marginDelegate;
        case AccountSummaryTablesEnum.AccountActivity:
            return this.activityDelegate;
        case AccountSummaryTablesEnum.TodaysResults:
            return this.todaysResultsDelegate;

        default:
            return this.generalDelegate;
        }
    }

    private createSummaryItem (name: string,
        wValue: IWatchedValueReadonly<number>,
        formatter: StandardFormatterName = StandardFormatterName.Fixed,
        isDefault: boolean = true): AccountManagerSummaryField {
        const summaryItem: AccountManagerSummaryField = {
            text: name,
            wValue,
            formatter,
            isDefault
        };

        return summaryItem;
    }

    private getTableTitle (tableType: AccountSummaryTablesEnum): string {
        switch (tableType) {
        case AccountSummaryTablesEnum.General:
            return Resources.getResource('panel.accountDetails.Groups.1.General');
        case AccountSummaryTablesEnum.Margin:
            return Resources.getResource('panel.accountDetails.Groups.2.Margin');
        case AccountSummaryTablesEnum.AccountActivity:
            return Resources.getResource('panel.accountDetails.Groups.3.AccountActivity');
        case AccountSummaryTablesEnum.TodaysResults:
            return Resources.getResource('panel.accountDetails.Groups.4.TodayResults');
        default:
            throw new Error('Invalid Table Type');
        }
    }
}
